import type { Element, LineElement } from '@/components/canvas/document/publicTypes';

function rectsIntersect(
  ax: number,
  ay: number,
  aw: number,
  ah: number,
  bx: number,
  by: number,
  bw: number,
  bh: number,
): boolean {
  return ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;
}

export interface MarqueeRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface MarqueePageSlice {
  id: string;
  workspaceLayout: { x: number; y: number };
  size: { width: number; height: number };
  elements: Element[];
}

export type MarqueeScope = 'page' | 'workspace';

/** 元素 AABB（页内或世界坐标，取决于传入的 layout） */
export function getElementAABB(el: Element): { x: number; y: number; w: number; h: number } {
  if (el.type === 'line') {
    const line = el as LineElement;
    const endX = line.data.end?.x ?? line.size.width;
    const endY = line.data.end?.y ?? 0;
    return {
      x: el.layout.x + Math.min(0, endX),
      y: el.layout.y + Math.min(0, endY),
      w: Math.max(1, Math.abs(endX)),
      h: Math.max(1, Math.abs(endY)),
    };
  }
  return { x: el.layout.x, y: el.layout.y, w: el.size.width, h: el.size.height };
}

export { rectsIntersect };

function pointInRect(px: number, py: number, r: MarqueeRect): boolean {
  return px >= r.x && px <= r.x + r.width && py >= r.y && py <= r.y + r.height;
}

/** Page 板面（不含标题栏）世界坐标矩形 */
export function getPageSurfaceWorldRect(page: MarqueePageSlice): MarqueeRect {
  const { x, y } = page.workspaceLayout;
  return { x, y, width: page.size.width, height: page.size.height };
}

/** 将页内元素转为世界坐标 */
export function toWorldElement(el: Element, pageX: number, pageY: number): Element {
  return {
    ...el,
    layout: {
      ...el.layout,
      x: pageX + el.layout.x,
      y: pageY + el.layout.y,
    },
  };
}

/**
 * 圈选范围分类：起点与终点均在当前页板面内 → 页面内圈选；否则工作区圈选。
 */
export function classifyMarqueeScope(
  start: { x: number; y: number },
  end: { x: number; y: number },
  currentPageId: string,
  pages: MarqueePageSlice[],
): MarqueeScope {
  const page = pages.find((p) => p.id === currentPageId);
  if (!page) return 'workspace';
  const surface = getPageSurfaceWorldRect(page);
  const startIn = pointInRect(start.x, start.y, surface);
  const endIn = pointInRect(end.x, end.y, surface);
  return startIn && endIn ? 'page' : 'workspace';
}

/**
 * 框选候选：组内编辑仅当前页子元素；否则按 scope 决定。
 */
export function getMarqueeCandidates(
  pages: MarqueePageSlice[],
  editingGroupId: string | null,
  currentPageId: string,
  scope: MarqueeScope,
): Element[] {
  if (editingGroupId) {
    const page = pages.find((p) => p.id === currentPageId);
    if (!page) return [];
    const px = page.workspaceLayout.x;
    const py = page.workspaceLayout.y;
    const grp = page.elements.find((e) => e.id === editingGroupId);
    if (!grp) return [];
    const grpWorld = toWorldElement(grp, px, py);
    return page.elements
      .filter(
        (el) =>
          !el.interaction.locked && el.interaction.visible !== false && el.layout.parentId === editingGroupId,
      )
      .map((child) => ({
        ...toWorldElement(child, px, py),
        layout: {
          ...child.layout,
          x: grpWorld.layout.x + child.layout.x,
          y: grpWorld.layout.y + child.layout.y,
        },
      }));
  }

  const pageList = scope === 'page' ? pages.filter((p) => p.id === currentPageId) : pages;

  const result: Element[] = [];
  for (const page of pageList) {
    const { x: px, y: py } = page.workspaceLayout;
    for (const el of page.elements) {
      if (el.interaction.locked || el.interaction.visible === false || el.layout.parentId) continue;
      result.push(toWorldElement(el, px, py));
    }
  }
  return result;
}

export function getWorkspaceElementCandidates(workspaceElements: Element[]): Element[] {
  return workspaceElements.filter((el) => !el.interaction.locked && el.interaction.visible !== false);
}

export function matchElementsInMarquee(
  candidates: Element[],
  mr: { x: number; y: number; w: number; h: number },
): string[] {
  if (mr.w <= 0 && mr.h <= 0) return [];
  return candidates
    .filter((el) => {
      const { x, y, w, h } = getElementAABB(el);
      return rectsIntersect(x, y, w, h, mr.x, mr.y, mr.w, mr.h);
    })
    .map((el) => el.id);
}

export function matchPagesInMarquee(
  pages: MarqueePageSlice[],
  mr: { x: number; y: number; w: number; h: number },
): string[] {
  if (mr.w <= 0 && mr.h <= 0) return [];
  return pages
    .filter((page) => {
      const surface = getPageSurfaceWorldRect(page);
      return rectsIntersect(surface.x, surface.y, surface.width, surface.height, mr.x, mr.y, mr.w, mr.h);
    })
    .map((p) => p.id);
}

export interface MarqueeMatchResult {
  scope: MarqueeScope;
  elementIds: string[];
  pageIds: string[];
}

export function resolveMarqueeSelection(
  pages: MarqueePageSlice[],
  workspaceElements: Element[],
  currentPageId: string,
  editingGroupId: string | null,
  start: { x: number; y: number },
  end: { x: number; y: number },
  mr: { x: number; y: number; w: number; h: number },
): MarqueeMatchResult {
  const scope = classifyMarqueeScope(start, end, currentPageId, pages);
  // 工作区圈选：仅命中 Page / workspace 根元素；页内子元素只随 Page 选中，不写入 elementIds
  const elementCandidates =
    scope === 'workspace'
      ? getWorkspaceElementCandidates(workspaceElements)
      : getMarqueeCandidates(pages, editingGroupId, currentPageId, scope);
  const elementIds = matchElementsInMarquee(elementCandidates, mr);
  const pageIds =
    scope === 'workspace' && !editingGroupId
      ? matchPagesInMarquee(pages, mr)
      : [];
  return { scope, elementIds, pageIds };
}
